import type {
  ChangeEvent,
  ComponentPropsWithoutRef,
  DragEvent,
  InputHTMLAttributes,
  ReactNode,
  Ref,
} from 'react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import * as React from 'react';
import { add, identity, isFunction, noop, partial } from 'lodash';
import styled from 'styled-components';
import {
  Box,
  ToastAlert,
  useId,
  useToast,
  VisuallyHidden,
} from '@frameio/vapor';
import TEXT from './generated/TEXT/FileDropZone';

const inc = partial(add, 1);
const dec = partial(add, -1);

const DropZoneBox = styled(Box)`
  /* hide the blue & white focus border of the file input. */
  outline: none;
`;

/**
 * @note The `behavior` prop exposes an interface for determining how the `FileDropZone` should be
 * rendered. Each `behavior` corresponds to an HTML element tag name.
 */
const HTML_TAGS_FOR_DROPZONE_BEHAVIOR: Record<
  string,
  keyof HTMLElementTagNameMap
> = {
  /**
   * `button` renders the `FileDropZone` root as a `label`, enabling native browser interactions
   * with the dropzone as a `button` like element which opens the native file system selector, etc.
   */
  button: 'label',
  /**
   * `display` renders the `FileDropZone` root as a `div`. This can be useful when rendering
   * some UI that implements non-native browser interactions for file inputs, e.g., an image editor.
   */
  display: 'div',
};

export type FileDropZoneProps = Pick<
  InputHTMLAttributes<HTMLInputElement>,
  'name' | 'accept'
> &
  Omit<
    // since this is a forwardRef component, we need to exclude the `ref` prop
    ComponentPropsWithoutRef<typeof Box>,
    | 'as'
    | 'children'
    // force the type of onChange in props object above
    | 'onChange'
  > & {
    allowMultiple?: InputHTMLAttributes<HTMLInputElement>['multiple'];
    children:
      | ((renderProps: { canDrop?: boolean; isFocused?: boolean }) => ReactNode)
      | ReactNode;
    invalidTypeMessage?: string;
    behavior?: keyof typeof HTML_TAGS_FOR_DROPZONE_BEHAVIOR;
    onChange?: (files: File | File[]) => void;
    handleInvalidFiles?: (invalidFiles: File[], validFiles: File[]) => void;
    validate?: (file: File) => boolean;
    isDisabled?: boolean;
  };

function FileDropZoneWithRef(
  {
    accept,
    allowMultiple = true,
    autoFocus = false,
    children,
    invalidTypeMessage,
    name,
    behavior = 'button',
    onChange = noop,
    validate = identity,
    isDisabled = false,
    handleInvalidFiles = null,
    ...rest
  }: FileDropZoneProps,
  forwardedRef: Ref<Pick<HTMLInputElement, 'click'>>
) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { addToast } = useToast();
  const labelId = useId();
  const [isFocused, setIsFocused] = useState(autoFocus);

  useImperativeHandle(forwardedRef, () => ({
    click: () => fileInputRef.current.click(),
  }));

  // Below we assign a counter that gets incremented/decremented as a user
  // drags into and out of the drop area. This is used to derive the
  // `canDrop` prop provided to children.
  // We reference a counter here instead of, e.g., a boolean value, in order
  // to work around how the `dragenter` and `dragleave` events behave relative
  // to parent and child elements. the implementation strategy here is based
  // on the example demonstrated in the SO comment here:
  // https://stackoverflow.com/a/21002544
  const [dropDepthCounter, setDropDepthCounter] = useState(0);

  const handleEnter = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDropDepthCounter(inc);
  };

  const handleLeave = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDropDepthCounter(dec);
  };

  // We're handling input here for either a dropped file over the
  // drop zone *or* direct interaction with the file input element
  const handleFile = (
    event: DragEvent<HTMLElement> | ChangeEvent<HTMLInputElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (isDisabled) {
      return;
    }

    // Conditionally assign file based on the shape of the event
    // being passed to `handleFile`.
    let files: FileList;

    if ('dataTransfer' in event) {
      // `dataTransfer` is available during events like drag/drop
      files = event.dataTransfer.files;
    } else {
      files = event.target.files;
    }
    const [validFiles, invalidFiles] = Array.from(files).reduce(
      (acc, file) => {
        if (validate(file)) {
          acc[0].push(file);
        } else {
          acc[1].push(file);
        }
        return acc;
      },
      [[] as File[], [] as File[]]
    );

    if (invalidFiles.length > 0) {
      if (isFunction(handleInvalidFiles)) {
        handleInvalidFiles(invalidFiles, validFiles);
      } else {
        addToast(
          (closeToast, toastCloseLabel) => (
            <ToastAlert
              closeLabel={toastCloseLabel}
              onRequestClose={closeToast}
              title={invalidTypeMessage}
              variant="warning"
            />
          ),
          { duration: 2000 }
        );
      }
    } else {
      onChange(allowMultiple ? validFiles : validFiles[0]);
    }
    setDropDepthCounter(0);
  };

  /*
   * Implement only when the DropZone is being applied as a "label" hit target.
   *
   * Forward spacebar and enter presses as a `click` on the file input
   * element in order to maintain keyboard accessibility.
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Disable default some browsers detect e.key === 'enter' as an open event
    e.preventDefault();
    if (e.key === 'Enter' || e.key === ' ') {
      fileInputRef.current.click();
    }
  };

  return (
    <DropZoneBox
      as={HTML_TAGS_FOR_DROPZONE_BEHAVIOR[behavior] || 'label'}
      id={labelId}
      onBlur={() => setIsFocused(false)}
      onDragEnter={handleEnter}
      onDragLeave={handleLeave}
      onDragOver={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onDrop={handleFile}
      onFocus={() => setIsFocused(true)}
      onKeyDown={behavior === 'button' ? handleKeyDown : noop}
      tabIndex={0}
      {...rest}
    >
      {typeof children === 'function'
        ? children({
            canDrop: dropDepthCounter > 0 && !isDisabled,
            isFocused,
          })
        : children}
      <VisuallyHidden>
        {isDisabled ? null : (
          <input
            ref={fileInputRef}
            accept={accept}
            aria-label={TEXT.uploadAsset}
            aria-labelledby={labelId}
            multiple={allowMultiple}
            name={name}
            onChange={handleFile}
            tabIndex={-1}
            type="file"
          />
        )}
      </VisuallyHidden>
    </DropZoneBox>
  );
}

TEXT.DEFINE({
  /* Accessible label for the hidden file input inside the drop zone. Not visible on screen */
  uploadAsset: 'Upload Asset',
});

export const FileDropZone = forwardRef(FileDropZoneWithRef);
